import Badge from "@intility/bifrost-react/Badge";
import Button from "@intility/bifrost-react/Button";
import Checkbox from "@intility/bifrost-react/Checkbox";
import Grid from "@intility/bifrost-react/Grid";
import Icon from "@intility/bifrost-react/Icon";
import Inline from "@intility/bifrost-react/Inline";
import Input from "@intility/bifrost-react/Input";
import Message from "@intility/bifrost-react/Message";
import Table from "@intility/bifrost-react/Table";
import Tooltip from "@intility/bifrost-react/Tooltip";
import Select from "@intility/bifrost-react-select";
import { useMemo, useState } from "react";
import {
  makePlatformAvailable,
  useCompanies,
  useSeededCompanies,
} from "~/api/catalog";
import type { SeedResult } from "~/api/fga-types";

interface Props {
  platformSlug: string;
}

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "available", label: "Available" },
  { value: "unavailable", label: "Not available" },
];

type StatusFilter = (typeof STATUS_FILTER_OPTIONS)[number];
type StatusSort = "asc" | "desc";

export default function SeedPanel({ platformSlug }: Props) {
  const { data: catalogCompanies, isLoading: namesLoading } = useCompanies();
  const {
    data: availability,
    isLoading: seededLoading,
    error: seededError,
  } = useSeededCompanies(platformSlug);
  const seeded = availability?.seeded;

  // The rows are the onboarded companies from the access store. The company list from
  // fga-api only gives display names. A company without a name shows its id.
  const companies = useMemo(() => {
    if (!availability) return undefined;
    const names = new Map(
      (catalogCompanies ?? []).map((c) => [c.id, c.displayName]),
    );
    return availability.companies.map((slug) => ({
      slug,
      displayName: names.get(slug) ?? slug,
    }));
  }, [availability, catalogCompanies]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [seeding, setSeeding] = useState(false);
  const [result, setResult] = useState<SeedResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(
    STATUS_FILTER_OPTIONS[0],
  );
  const [statusSort, setStatusSort] = useState<StatusSort>("asc");

  const seededSet = useMemo(() => new Set(seeded ?? []), [seeded]);
  // Only companies the platform is not yet available for can be selected.
  const selectable = (companies ?? []).filter((c) => !seededSet.has(c.slug));
  const anySelectable = selectable.length > 0;

  // Companies shown under the active search and status filter. Available
  // companies sort above not available ones ("asc"); within a status,
  // companies stay alphabetical by display name.
  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (companies ?? [])
      .filter((c) => {
        const available = seededSet.has(c.slug);
        if (statusFilter.value === "available" && !available) return false;
        if (statusFilter.value === "unavailable" && available) return false;
        if (!query) return true;
        return (
          c.displayName.toLowerCase().includes(query) ||
          c.slug.toLowerCase().includes(query)
        );
      })
      .sort((a, b) => {
        const aAvailable = seededSet.has(a.slug);
        const bAvailable = seededSet.has(b.slug);
        if (aAvailable !== bAvailable) {
          if (statusSort === "asc") return aAvailable ? -1 : 1;
          return aAvailable ? 1 : -1;
        }
        return a.displayName.localeCompare(b.displayName);
      });
  }, [companies, search, statusFilter, statusSort, seededSet]);

  const toggle = (slug: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  // Select every selectable company on screen, not every selectable company:
  // with an active search or filter the hidden rows stay untouched.
  const selectAll = () =>
    setSelected(
      new Set(visible.filter((c) => !seededSet.has(c.slug)).map((c) => c.slug)),
    );

  const deselectAll = () => setSelected(new Set());

  const handleSeed = async () => {
    const targets = [...selected].filter((slug) => !seededSet.has(slug));
    if (seeding || targets.length === 0) return;
    setSeeding(true);
    setError(null);
    setResult(null);
    try {
      if (companies && targets.length === companies.length) {
        const res = await makePlatformAvailable(platformSlug, { all: true });
        setResult(res);
      } else {
        const results: SeedResult = { seededCompanies: [] };
        for (const companySlug of targets) {
          const res = await makePlatformAvailable(platformSlug, {
            companySlug,
          });
          results.seededCompanies.push(...res.seededCompanies);
        }
        setResult(results);
      }
      setSelected(new Set());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSeeding(false);
    }
  };

  if (namesLoading || seededLoading) {
    return (
      <Grid gap={16} style={{ minHeight: 100, placeContent: "center" }}>
        <Icon.Spinner size={24} />
      </Grid>
    );
  }

  if (seededError) {
    return (
      <Message state="alert" header="Could not load availability">
        {seededError instanceof Error
          ? seededError.message
          : String(seededError)}
      </Message>
    );
  }

  if (!companies || companies.length === 0) {
    return (
      <Message state="neutral">
        No companies are onboarded. Onboard companies on the Companies page
        first.
      </Message>
    );
  }

  const seededCount = seededSet.size;

  return (
    <Grid gap={16}>
      <p className="bf-p" style={{ color: "var(--bfc-base-c-2)", margin: 0 }}>
        The companies below are the companies that will use this platform.
        Making the platform available for a company creates the company link and
        the static resources in the access store. It is safe to repeat this
        step.
      </p>
      <ul
        className="bf-p"
        style={{ color: "var(--bfc-base-c-2)", margin: 0, paddingLeft: 16 }}
      >
        <li>
          If your platform is customer facing, you probably want to make it
          available for all companies.
        </li>
        <li>
          If your platform is for internal use, it is standard to make it
          available for Intility only. Skoglab and Nimpos can also be good
          choices if you need more than one company context.
        </li>
      </ul>
      <Message
        state="attn"
        header="This does not control who can access your platform"
      >
        It only controls which companies this platform and its resources are
        located under.
      </Message>

      {anySelectable ? (
        <Inline align="center">
          <span
            className="bf-p"
            style={{ color: "var(--bfc-base-c-2)", margin: 0 }}
          >
            Available for {seededCount} of {companies.length} companies
          </span>
          <Inline.Separator />
          <Inline gap={8}>
            <Button small variant="flat" onClick={selectAll}>
              Select all
            </Button>
            <Button small variant="flat" onClick={deselectAll}>
              Deselect all
            </Button>
          </Inline>
        </Inline>
      ) : (
        <p className="bf-p" style={{ margin: 0 }}>
          Available for all {companies.length} companies.
        </p>
      )}

      <div
        style={{
          display: "flex",
          gap: 16,
          flexWrap: "wrap",
          alignItems: "flex-end",
        }}
      >
        <Input
          label="Search"
          placeholder="Company name or ID…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 320 }}
        />
        <Select
          label="Status"
          options={STATUS_FILTER_OPTIONS}
          value={statusFilter}
          onChange={(opt) => opt && setStatusFilter(opt as StatusFilter)}
          style={{ maxWidth: 220 }}
        />
      </div>

      <Table>
        <Table.Header>
          <Table.Row>
            {anySelectable && <Table.HeaderCell />}
            <Table.HeaderCell>Company</Table.HeaderCell>
            <Table.HeaderCell>
              <Tooltip content="The company GUID from Company Service.">
                <span>Company ID</span>
              </Tooltip>
            </Table.HeaderCell>
            <Table.HeaderCell
              sorting={statusSort}
              onClick={() =>
                setStatusSort(statusSort === "asc" ? "desc" : "asc")
              }
            >
              Status
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {visible.length === 0 ? (
            <Table.Row>
              {anySelectable && <Table.Cell />}
              <Table.Cell>No companies match your search or filter.</Table.Cell>
              <Table.Cell />
              <Table.Cell />
            </Table.Row>
          ) : (
            visible.map((c) => {
              const available = seededSet.has(c.slug);
              return (
                <Table.Row key={c.slug}>
                  {anySelectable && (
                    <Table.Cell>
                      {!available && (
                        <Checkbox
                          label={`Make available for ${c.displayName}`}
                          hideLabel
                          checked={selected.has(c.slug)}
                          onChange={() => toggle(c.slug)}
                        />
                      )}
                    </Table.Cell>
                  )}
                  <Table.Cell>{c.displayName}</Table.Cell>
                  <Table.Cell>
                    <code>{c.slug}</code>
                  </Table.Cell>
                  <Table.Cell>
                    {available ? (
                      <Badge state="success">Available</Badge>
                    ) : (
                      <Badge state="neutral">Not available</Badge>
                    )}
                  </Table.Cell>
                </Table.Row>
              );
            })
          )}
        </Table.Body>
      </Table>

      {anySelectable && (
        <Button
          onClick={handleSeed}
          state={seeding || selected.size === 0 ? "inactive" : undefined}
        >
          {seeding
            ? "Making available…"
            : `Make available to ${selected.size} ${selected.size === 1 ? "company" : "companies"}`}
        </Button>
      )}

      {error && (
        <Message state="alert" header="Could not make available">
          {error}
        </Message>
      )}

      {result && (
        <Message
          state={
            result.seededCompanies.some((sc) => sc.tuplesSkipped > 0)
              ? "neutral"
              : "success"
          }
          header="Platform made available"
        >
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            {result.seededCompanies.map((sc) => (
              <li key={sc.companySlug}>
                <code>{sc.companySlug}</code>: {sc.tuplesWritten} tuples written
                {sc.tuplesSkipped > 0 &&
                  `, ${sc.tuplesSkipped} already existed`}
              </li>
            ))}
          </ul>
        </Message>
      )}
    </Grid>
  );
}
